import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, CertificateVerification } from "../lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Award,
  CheckCircle,
  XCircle,
  Search,
  AlertTriangle,
} from "lucide-react";

export default function VerifyCertificate() {
  const [searchParams] = useSearchParams();
  const [certNumber, setCertNumber] = useState(
    searchParams.get("number") || "",
  );
  const [result, setResult] = useState<CertificateVerification | null>(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    const number = searchParams.get("number");
    if (number) {
      setCertNumber(number);
      handleVerify(number);
    }
  }, [searchParams]);

  const handleVerify = async (number?: string) => {
    const query = number || certNumber.trim();
    if (!query) return;

    setLoading(true);
    setSearched(true);
    try {
      const data = await api.verifyCertificate(query);
      setResult(data);
    } catch (error: any) {
      setResult({
        valid: false,
        error: error.message || "Verification failed",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <div className="text-center mb-8">
        <Award className="w-12 h-12 text-blue-600 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Verify Certificate
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Enter a certificate number to verify its authenticity.
        </p>
      </div>

      {/* Search box */}
      <div className="flex gap-3 mb-8">
        <input
          type="text"
          value={certNumber}
          onChange={(e) => setCertNumber(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleVerify()}
          placeholder="ITHOPU-TC-2026-00123"
          className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
        />
        <Button onClick={() => handleVerify()} disabled={loading || !certNumber.trim()}>
          <Search className="w-4 h-4 mr-2" />
          {loading ? "Checking..." : "Verify"}
        </Button>
      </div>

      {/* Result */}
      {searched && result && !loading && (
        <Card
          className={
            result.valid
              ? "border-green-300 dark:border-green-700"
              : "border-red-300 dark:border-red-700"
          }
        >
          <CardContent className="p-8">
            {result.valid && result.certificate ? (
              <div>
                <div className="text-center mb-6">
                  <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-3" />
                  <h2 className="text-xl font-bold text-green-700 dark:text-green-300">
                    Valid Certificate
                  </h2>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Holder</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {result.certificate.holder_name}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Certificate</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {result.certificate.title}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Type</span>
                    <span className="font-medium text-gray-900 dark:text-white capitalize">
                      {result.certificate.certificate_type} Certificate
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Accuracy</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {result.certificate.accuracy.toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Score</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {result.certificate.problems_correct}/
                      {result.certificate.problems_attempted}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Issue Date</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {new Date(
                        result.certificate.issued_at,
                      ).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Status</span>
                    <span
                      className={`font-medium ${
                        result.certificate.status === "valid"
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {result.certificate.status.charAt(0).toUpperCase() +
                        result.certificate.status.slice(1)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Certificate Number</span>
                    <span className="font-mono font-medium text-gray-900 dark:text-white">
                      {result.certificate.certificate_number}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center">
                {result.error?.includes("not found") ? (
                  <>
                    <XCircle className="w-16 h-16 text-red-500 mx-auto mb-3" />
                    <h2 className="text-xl font-bold text-red-700 dark:text-red-300 mb-2">
                      Certificate Not Found
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                      No certificate matches this number. Please check and try
                      again.
                    </p>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-3" />
                    <h2 className="text-xl font-bold text-amber-700 dark:text-amber-300 mb-2">
                      Verification Failed
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                      {result.error}
                    </p>
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
